const adminModelKeys = {
  actions: {
    add: 'New distribution key',
    delete: 'Delete',
    edit: 'Edit',
    open: 'Open actions for {{name}}',
    rotate: 'Rotate key',
  },
  columnSelector: {
    label: 'Choose model routing columns',
    trigger: 'Columns',
  },
  deleteDialog: {
    cancel: 'Cancel',
    close: 'Close delete confirmation',
    confirm: 'Delete',
    failed: 'Failed to delete distribution key',
    message:
      'Delete distribution key {{name}}? Clients using this key will stop working immediately.',
    subtitle: 'This deletes only the distribution key. Registered models are not deleted.',
    success: 'Distribution key deleted',
    title: 'Delete distribution key?',
  },
  description: 'Manage external distribution keys, output protocols, allowed models, and quotas.',
  drawer: {
    cancel: 'Cancel',
    createTitle: 'New distribution key',
    detailLoadFailed:
      'Try again later, or check whether this admin still has model gateway access.',
    detailLoadFailedTitle: 'Failed to load distribution key detail',
    editTabs: {
      basic: 'Basic config',
      label: 'Distribution key edit tabs',
      models: 'Model selection',
      quota: 'Quota rules',
      view: 'View',
    },
    editTitle: 'Edit / {{name}}',
    feedback: {
      createSuccess: 'Distribution key created',
      submitFailed: 'Save failed',
      updateSuccess: 'Distribution key saved',
      validationFailed: 'Fix the form first',
    },
    fields: {
      enabled: 'Enabled',
      name: 'Key name',
      outputProtocol: 'Output protocol',
    },
    models: {
      description: 'Choose registered models this key can call. The gateway routes requests.',
      empty: 'No registered models available',
      noMatches: 'No matching registered models',
      searchLabel: 'Search available models',
      searchPlaceholder: 'Search model or interface protocol',
      title: 'Allowed models',
      unavailable: 'Disabled or unavailable',
    },
    placeholders: {
      name: 'For example: platform-internal-eval',
    },
    save: 'Save',
    submitFailed: 'Failed to save the distribution key. Try again later.',
    validation: {
      allowedModelsRequired: 'Select at least one allowed model',
      allowedModelsUnavailable: 'Remove disabled or unavailable models',
      nameRequired: 'Enter a key name',
      protocolRequired: 'Select an output protocol',
      quotaActionRequired: 'Select a quota action',
      quotaCurrencyRequired: 'Cost quota rules require a currency',
      quotaMetricRequired: 'Select a quota metric',
      quotaScopeRequired: 'Select a quota scope',
      quotaThresholdRequired: 'Enter a valid threshold',
      quotaWindowRequired: 'Select a quota window',
    },
    wizard: {
      create: 'Create key',
      headerDescription: 'Step {{current}} / {{total}}',
      next: 'Next',
      previous: 'Previous',
      progressLabel:
        'New distribution key progress: step {{current}} of {{total}}, currently {{step}}',
      steps: {
        key: {
          title: 'Basic config',
        },
        models: {
          title: 'Model config',
        },
        quota: {
          title: 'Quota rules',
        },
      },
    },
    view: {
      copyEndpoint: 'Copy endpoint',
      copyFailed: 'Copy failed. Copy it manually.',
      copyModel: 'Copy model name {{name}}',
      copySuccess: 'Copied',
      endpoint: 'Endpoint',
      endpointDescription:
        'Clients call the model gateway with this endpoint and distribution key.',
      endpointTitle: 'Invocation endpoint',
      modelsDescription: 'Use one of these public model names in the request model field.',
      modelsEmpty: 'No allowed models.',
      modelsTitle: 'Allowed model names',
    },
  },
  empty: 'No distribution keys yet',
  errorDescription: 'Try again later, or check whether this admin still has access.',
  errorDescriptionWithDetail: 'Backend response: {{detail}}',
  errorTitle: 'Failed to load distribution keys',
  filters: {
    allStatuses: 'All statuses',
    searchLabel: 'Search distribution keys',
    searchPlaceholder: 'Search key name, prefix, or protocol',
    statusLabel: 'Filter key status',
  },
  pagination: {
    next: 'Next',
    pageSize: '{{count}} / page',
    previous: 'Previous',
  },
  quota: {
    action: 'Action',
    actions: {
      block: 'Block request',
      notify_only: 'Notify only',
    },
    addRule: 'Add rule',
    currency: 'Currency',
    currencyPlaceholder: 'For example: USD',
    description: 'Limit this key by request count, tokens, cost, or concurrency.',
    disabled: 'Disabled',
    empty: 'No quota rules configured.',
    enabled: 'Enabled',
    enabledLabel: 'Toggle quota rule {{index}}',
    metric: 'Metric',
    metrics: {
      completion_tokens: 'Completion tokens',
      concurrency: 'Concurrency',
      cost_amount: 'Cost amount',
      prompt_tokens: 'Prompt tokens',
      request_count: 'Requests',
      total_tokens: 'Total tokens',
    },
    priority: 'Priority',
    removeRule: 'Remove',
    ruleTitle: 'Rule {{index}}',
    scope: 'Scope',
    scopeValue: 'Scope value',
    scopeValuePlaceholder: 'Optional model name or model ID',
    scopes: {
      all_models: 'All models',
      per_public_model: 'Per display model',
      per_registered_model: 'Per registered model',
    },
    threshold: 'Threshold',
    title: 'Quota rules',
    window: 'Window',
    windows: {
      all_time: 'All time',
      day: 'Daily',
      hour: 'Hourly',
      minute: 'Per minute',
      month: 'Monthly',
    },
  },
  reveal: {
    close: 'Close plaintext key dialog',
    confirm: 'I have saved it',
    copyFailed: 'Copy failed. Copy it manually.',
    copySecret: 'Copy plaintext key',
    copySuccess: 'Distribution key copied',
    description:
      'The plaintext key is shown only after creation or rotation. Save it securely before leaving the page.',
    secret: 'Plaintext key',
    title: 'Plaintext key for {{name}}',
  },
  retry: 'Retry',
  rotateDialog: {
    cancel: 'Cancel',
    close: 'Close rotation confirmation',
    confirm: 'Rotate',
    failed: 'Failed to rotate distribution key',
    message: 'Rotate distribution key {{name}}? The old key will be revoked immediately.',
    subtitle: 'A new plaintext key will be shown after rotation. Update clients promptly.',
    success: 'Distribution key rotated',
    title: 'Rotate distribution key?',
  },
  status: {
    active: 'Enabled',
    disabled: 'Disabled',
  },
  table: {
    actions: 'Actions',
    allowedModels: 'Allowed models',
    createdAt: 'Created at',
    createdBy: 'Creator',
    key: 'Key',
    name: 'Key name',
    noQuota: 'None',
    outputProtocol: 'Output protocol',
    quota: 'Quota',
    status: 'Status',
  },
  title: 'Model Routing',
  workspaceLabel: 'Model routing workspace',
}

export default adminModelKeys
